import { createSelector } from '@ngrx/store';

import { selectUser } from '@/app/dashboard/store/dashboard.selectors';
import { UserRole } from '@/app/dashboard/models/dashboard.model';

import {
  NotificationPermissionEntry,
  PERMISSIONS_CONFIG,
  PermissionEntry,
} from './permission.types';

/**
 * Build the role → permission-id Set lookup once at module load. NgRx
 * selectors are memoised, but the underlying derivation runs every time the
 * memo key (the user object) changes — pre-computing this index keeps that
 * derivation O(1).
 */
const ROLE_TO_PERMISSIONS: ReadonlyMap<
  UserRole,
  ReadonlySet<string>
> = buildRoleIndex(PERMISSIONS_CONFIG.permissions);

const ROLE_TO_NOTIFICATIONS: ReadonlyMap<
  UserRole,
  ReadonlySet<string>
> = buildRoleIndex(PERMISSIONS_CONFIG.notifications);

const EMPTY: ReadonlySet<string> = new Set<string>();

/**
 * Set of permission ids the currently signed-in user may use, derived from
 * the user's role. Returns an empty set while the profile is still
 * loading; consumers must not assume "no permissions" means "denied
 * forever" — branch on the user/loading state when timing matters.
 */
export const selectCurrentUserPermissions = createSelector(
  selectUser,
  (user): ReadonlySet<string> => {
    if (!user?.role) {
      return EMPTY;
    }
    return ROLE_TO_PERMISSIONS.get(user.role) ?? EMPTY;
  }
);

/**
 * Set of notification ids the currently signed-in user is eligible to
 * receive in the bell/toast layer based on role. The server is still the
 * authoritative source of delivery; this filter only drops events that
 * leaked through (or that the dispatcher should not surface to this role).
 */
export const selectCurrentUserAllowedNotifications = createSelector(
  selectUser,
  (user): ReadonlySet<string> => {
    if (!user?.role) {
      return EMPTY;
    }
    return ROLE_TO_NOTIFICATIONS.get(user.role) ?? EMPTY;
  }
);

function buildRoleIndex<T extends { id: string; allowedRoles: UserRole[] }>(
  entries: T[]
): ReadonlyMap<UserRole, ReadonlySet<string>> {
  const index = new Map<UserRole, Set<string>>();
  for (const entry of entries) {
    for (const role of entry.allowedRoles) {
      let bucket = index.get(role);
      if (!bucket) {
        bucket = new Set<string>();
        index.set(role, bucket);
      }
      bucket.add(entry.id);
    }
  }
  return index;
}

/**
 * Test helper — returns the raw permission entry for an id (or `undefined`).
 * Not used by runtime checks; exposed so feature specs can assert the
 * matrix they depend on.
 */
export function getPermissionEntry(id: string): PermissionEntry | undefined {
  return PERMISSIONS_CONFIG.permissions.find(p => p.id === id);
}

export function getNotificationPermissionEntry(
  id: string
): NotificationPermissionEntry | undefined {
  return PERMISSIONS_CONFIG.notifications.find(n => n.id === id);
}
